import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import MapDashboard from './pages/MapDashboard';
import Demands from './pages/Demands';
import Team from './pages/Team';
import Contacts from './pages/Contacts';
import Settings from './pages/Settings';
import Account from './pages/Account';
import RolesManagement from './pages/RolesManagement';
import Logo from './components/common/Logo';
import BottomNavigation from './components/common/BottomNavigation';
import ProtectedRoute from './components/ProtectedRoute';
import { AuthProvider } from './contexts/AuthContext';
import { PermissionsProvider } from './contexts/PermissionsContext';
import { ThemeProvider } from './contexts/ThemeContext';
import './App.css';

type TabType = 'map' | 'demands' | 'contacts' | 'account';

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<TabType>('map');
  const [showNavigation, setShowNavigation] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [scrollTimeout, setScrollTimeout] = useState<NodeJS.Timeout | null>(null);
  const [triggerAddDemand, setTriggerAddDemand] = useState(0);
  const [isMapSideSheetOpen, setIsMapSideSheetOpen] = useState(false);
  const [mapDisplayMode, setMapDisplayMode] = useState<'map' | 'list'>(() => {
    if (window.innerWidth < 768) return 'list';
    const saved = localStorage.getItem('displayMode');
    return (saved === 'map' || saved === 'list') ? saved : 'list';
  });
  const [contactsDisplayMode, setContactsDisplayMode] = useState<'map' | 'list'>(() => {
    if (window.innerWidth < 768) return 'list';
    const saved = localStorage.getItem('displayMode');
    return (saved === 'map' || saved === 'list') ? saved : 'list';
  });
  const [demandsDisplayMode, setDemandsDisplayMode] = useState<'map' | 'list'>(() => {
    if (window.innerWidth < 768) return 'list';
    const saved = localStorage.getItem('displayMode');
    return (saved === 'map' || saved === 'list') ? saved : 'list';
  });

  const isLoginPage = location.pathname === '/login';

  useEffect(() => {
    // Update active tab based on current path
    const path = location.pathname;
    if (path === '/mapa' || path === '/') {
      setActiveTab('map');
      setTriggerAddDemand(0);
    } else if (path === '/demandas') {
      setActiveTab('demands');
    } else if (path === '/contatos') {
      setActiveTab('contacts');
      setTriggerAddDemand(0);
    } else if (path === '/configuracoes') {
      setActiveTab('account');
      setTriggerAddDemand(0);
    }
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
        setScrollTimeout(null);
      }

      const isMapInListMode = (location.pathname === '/mapa' || location.pathname === '/') && mapDisplayMode === 'list';
      if (location.pathname === '/demandas' || isMapInListMode) {
        if (currentScrollY > lastScrollY && currentScrollY > 100) {
          setShowNavigation(false);
        } else if (currentScrollY < lastScrollY) {
          setShowNavigation(true);
        }

        const timeout = setTimeout(() => {
          setShowNavigation(true);
        }, 1500);

        setScrollTimeout(timeout);
      } else {
        setShowNavigation(true);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeout) clearTimeout(scrollTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastScrollY, location.pathname, mapDisplayMode]);

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    switch (tab) {
      case 'map':      navigate('/mapa'); break;
      case 'demands':  navigate('/demandas'); break;
      case 'contacts': navigate('/contatos'); break;
      case 'account':  navigate('/configuracoes'); break;
    }
  };

  const getPageTitle = () => {
    switch (activeTab) {
      case 'map':      return 'Cidades';
      case 'demands':  return 'Demandas';
      case 'contacts': return 'Contatos';
      case 'account':  return 'Configurações';
    }
  };

  const handleHeaderButtonClick = () => {
    setTriggerAddDemand(prev => prev + 1);
  };

  const handleMapSideSheetChange = (isOpen: boolean) => {
    setIsMapSideSheetOpen(isOpen);
  };

  const handleMapDisplayModeChange = (mode: 'map' | 'list') => {
    setMapDisplayMode(mode);
    setContactsDisplayMode(mode);
    setDemandsDisplayMode(mode);
    localStorage.setItem('displayMode', mode);
    if (mode === 'map') setShowNavigation(true);
  };

  const handleContactsDisplayModeChange = (mode: 'map' | 'list') => {
    setContactsDisplayMode(mode);
    setMapDisplayMode(mode);
    setDemandsDisplayMode(mode);
    localStorage.setItem('displayMode', mode);
    if (mode === 'map') setShowNavigation(true);
  };

  const handleDemandsDisplayModeChange = (mode: 'map' | 'list') => {
    setDemandsDisplayMode(mode);
    setMapDisplayMode(mode);
    setContactsDisplayMode(mode);
    localStorage.setItem('displayMode', mode);
    if (mode === 'map') setShowNavigation(true);
  };

  return (
    <div className="App">
      {!isLoginPage && (
        <Logo
          displayMode={activeTab === 'map' ? mapDisplayMode : activeTab === 'contacts' ? contactsDisplayMode : activeTab === 'demands' ? demandsDisplayMode : undefined}
          onDisplayModeChange={activeTab === 'map' ? handleMapDisplayModeChange : activeTab === 'contacts' ? handleContactsDisplayModeChange : activeTab === 'demands' ? handleDemandsDisplayModeChange : undefined}
        />
      )}
      <div style={{ paddingTop: isLoginPage ? 0 : 72 }}>
        <Routes>
          <Route path="/login" element={<Account />} />
          <Route path="/" element={<ProtectedRoute><MapDashboard onSideSheetChange={handleMapSideSheetChange} onDisplayModeChange={handleMapDisplayModeChange} displayMode={mapDisplayMode} onNavVisibilityChange={setShowNavigation} /></ProtectedRoute>} />
          <Route path="/mapa" element={<ProtectedRoute><MapDashboard onSideSheetChange={handleMapSideSheetChange} onDisplayModeChange={handleMapDisplayModeChange} displayMode={mapDisplayMode} onNavVisibilityChange={setShowNavigation} /></ProtectedRoute>} />
          <Route path="/demandas" element={<ProtectedRoute><Demands showHeaderButton={false} triggerAdd={triggerAddDemand} onHeaderButtonClick={handleHeaderButtonClick} displayMode={demandsDisplayMode} onDisplayModeChange={handleDemandsDisplayModeChange} /></ProtectedRoute>} />
          <Route path="/time" element={<ProtectedRoute><Team /></ProtectedRoute>} />
          <Route path="/contatos" element={<ProtectedRoute><Contacts displayMode={contactsDisplayMode} onDisplayModeChange={handleContactsDisplayModeChange} /></ProtectedRoute>} />
          <Route path="/configuracoes" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
          <Route path="/papeis" element={<ProtectedRoute><RolesManagement /></ProtectedRoute>} />
        </Routes>
      </div>
      {!isLoginPage && (
        <BottomNavigation
          activeTab={activeTab}
          onTabChange={handleTabChange}
          position={(activeTab === 'map' && mapDisplayMode === 'map' && !isMapSideSheetOpen) || (activeTab === 'contacts' && contactsDisplayMode === 'map') || (activeTab === 'demands' && demandsDisplayMode === 'map') ? 'right' : 'center'}
          visible={showNavigation}
        />
      )}
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ThemeProvider>
          <PermissionsProvider>
            <AppContent />
          </PermissionsProvider>
        </ThemeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
